import { getComponent, getUIComponent, validCFDElements, validRepeaterElements } from "@builder/store/slices/BuilderSlice/builderMap";
import applyDefaultStyles from "@builder/store/slices/BuilderSlice/reducers/newNodeDefaultStyle";
import { BuilderState } from "@builder/store/slices/BuilderSlice/types";
import store from "@builder/store/store";
import { ActiveEntityType, DynamicDataContext } from "@common/build/activeEntityType";
import { QueryDataTables } from "@common/build/constants";
import {
    BaseLayer,
    BaseLayerTypes,
    Build,
    BuildComponentKey,
    BuildNode,
    BuildTypes,
    GlobalBlockTypes,
    ImageNode,
    NodeSizes,
    ConditionalVariation
} from "@common/build/types";
import { v4 } from "uuid";
import { setActionFromNode, setCurrentPopup } from "../popupSlice";
import { setLeftInstance } from "../sidebarSlice";
import { CSSProperties } from "react";
import { attachmentsApi } from "@builder/store/slices/attachmentsApiSlice/attachmentsApiSlice";
import { RawBuild } from "../../../types/build.type";

export const actionQueueLimit = 10;

//Helper Functions

export const convertRawBuildToBuild = (rawBuild: RawBuild): Build => {
    const pages = rawBuild.pages ? JSON.parse(rawBuild.pages) : [];
    if (pages.length === 0) {
        if ([BuildTypes.GLOBAL_BLOCK, BuildTypes.ORG_GLOBAL_BLOCK, BuildTypes.OLIVER_GLOBAL_BLOCK].includes(rawBuild.buildType as BuildTypes)) {
            pages.push(layerInitialize(BaseLayerTypes.PAGES, "Print", GlobalBlockTypes.GB_PRINT));
        } else {
            pages.push(layerInitialize(BaseLayerTypes.PAGES, "Home"));
        }
    }

    const isSizeCustom = !rawBuild.size?.key || rawBuild.size?.key === "custom";
    return {
        id: rawBuild._id,
        name: rawBuild.name,
        version: rawBuild.version,
        size: {
            key: isSizeCustom ? "custom" : rawBuild.size?.key || "fullHD",
            frameSize: isSizeCustom ? { x: rawBuild.size?.frameSize?.x as number, y: rawBuild.size?.frameSize?.y as number } : { x: 1920, y: 1080 }
        },
        isPublished: rawBuild.isPublished ?? undefined,
        pages: pages,
        popups: rawBuild.popups ? JSON.parse(rawBuild.popups) : [],
        cfds: rawBuild.cfds ? JSON.parse(rawBuild.cfds) : [],
        specialLayers: rawBuild.specialLayers,
        previewImage: rawBuild.previewImage,
        companyId: rawBuild.companyId,
        accountId: rawBuild.accountId,
        buildType: rawBuild.buildType ? (rawBuild.buildType as BuildTypes) : BuildTypes.BUILD,
        globalBlockType: rawBuild.globalBlockType ? (rawBuild.globalBlockType as GlobalBlockTypes) : undefined,
        actions: rawBuild.actions,
        actionBuilder: rawBuild.actionBuilder ? JSON.parse(rawBuild.actionBuilder) : {},
        globalBlocks: rawBuild.globalBlocks ? JSON.parse(rawBuild.globalBlocks) : {},
        extraAttributes: rawBuild.extraAttributes || {},
        isPremium: rawBuild.isPremium,
        paymentGridSettings: rawBuild.paymentGridSettings,
        tag: rawBuild.tag
    };
};

export const deepCopy = (obj: object) => {
    return JSON.parse(JSON.stringify(obj));
};

export const findName = (layers: BaseLayer[], name: string, inPrentices?: boolean) => {
    const existingNames = layers.map(layer => layer.name.toLowerCase());
    let newName = name;
    let count = 1;

    while (existingNames.includes(newName.toLowerCase())) {
        newName = inPrentices ? `${name} (${count++})` : `${name} ${count++}`;
    }

    return newName;
};

export const deleteNodeHelper = (baseLayer: BaseLayer, nodeId: string): void => {
    const node = baseLayer.components[nodeId];
    if (!node) return;

    // Recursively delete all children first
    if (node.children) {
        for (const child of node.children) {
            deleteNodeHelper(baseLayer, child);
        }
    }

    // Remove this node from its parent's children array
    if (node.parent) {
        const parentNode = baseLayer.components[node.parent];
        if (parentNode && parentNode.children) {
            parentNode.children = parentNode.children.filter(childId => childId !== nodeId);
        }
    }

    // Finally delete the node from components
    delete baseLayer.components[nodeId];
};

export const getBaseLayer = (state: BuilderState): BaseLayer => {
    return state.build[state.currentLayer.type][state.currentLayer.index];
};

export const getLayer = (state: BuilderState, layerId: string): BaseLayer | null => {
    for (const layerType of Object.values(BaseLayerTypes)) {
        const layers = state.build[layerType];
        for (const layer of layers) {
            if (layer.id === layerId) {
                return layer;
            }
        }
    }
    return null;
};

export const getRootNode = (baseLayer: BaseLayer): BuildNode => {
    return baseLayer.components[baseLayer.root];
};

export const getSelectedNode = (state: BuilderState): BuildNode | undefined => {
    if (state.selectedNode) return getBaseLayer(state).components[state.selectedNode];
};

export const isValidParent = (baseLayer: BaseLayer, node: BuildNode, parentId: string, checkCycle?: boolean, cvModeActive?: boolean) => {
    const [id, _] = parentId.split("|") as [string, string];
    const parentNode = baseLayer.components[id];
    if (isBaseLayerRootNode(node)) return false; //base layer should not be a child of anything
    if (parentNode?.childrenFilterKey) return false; //should not be able to add new children to a node with childrenFilterKey - only we can.
    if (parentNode) {
        //Check if in repeater element
        if (isRepeaterElement(baseLayer, parentNode)) {
            if (!validRepeaterElements.includes(node.name)) {
                return false;
            }
        }

        //Check if restricted to specific parents
        const elementUI = getUIComponent(node.name as BuildComponentKey);
        if (elementUI.restrictToParents) {
            if (!isAncestorOf(baseLayer, elementUI.restrictToParents, parentNode)) {
                return false;
            }
        }

        //Check if in excluded list
        const parentUI = getUIComponent(parentNode.name as BuildComponentKey);
        if (parentUI.excludedChildren.length > 0) {
            if (parentUI.excludedChildren[0] === "*") {
                return false;
            } else if (parentUI.excludedChildren.includes(node.name)) {
                return false;
            }
        }

        //Check if not in allowed list of CFD base layer
        else if (baseLayer.components[baseLayer.root].name === "baseLayerCFD") {
            if (!validCFDElements.includes(node.name)) {
                return false;
            }
        }

        //Optionally check if parent is child of node
        if (checkCycle) {
            if (node.id === parentId || isAncestor(baseLayer, node.id, parentNode)) {
                return false;
            }
        }

        //Check if node has conditional variations but not in cv mode
        if (parentNode.conditionalVariations && parentNode.conditionalVariations.length > 0 && !cvModeActive) {
            return false;
        }

        return true;
    }

    return false;
};

export const isRepeaterElement = (baseLayer: BaseLayer, node: BuildNode) => {
    let currentNode = node;
    while (currentNode && node.id !== baseLayer.root) {
        if (currentNode.name === "repeaterDiv") {
            return true;
        } else {
            if (currentNode.parent) {
                currentNode = baseLayer.components[currentNode.parent];
            } else {
                return false;
            }
        }
    }

    return false;
};

export const isAncestor = (baseLayer: BaseLayer, nodeId: string, targetNode: BuildNode) => {
    let currentNode = targetNode;

    while (currentNode?.parent) {
        if (currentNode.parent === nodeId) {
            return true;
        } else {
            currentNode = baseLayer.components[currentNode.parent];
        }
    }

    return false;
};

// same thing but element by name:
export const isAncestorOf = (baseLayer: BaseLayer, ancestorName: BuildComponentKey | BuildComponentKey[], targetNode: BuildNode): boolean => {
    const isNameMatch = (node: BuildNode) => {
        if (Array.isArray(ancestorName)) {
            return ancestorName.includes(node.name as BuildComponentKey);
        }
        return node.name === ancestorName;
    };

    // Check the target node itself
    if (isNameMatch(targetNode)) {
        return true;
    }

    // Check ancestors
    let currentNode = targetNode;
    while (currentNode?.parent) {
        const parentNode = baseLayer.components[currentNode.parent];
        if (parentNode && isNameMatch(parentNode)) {
            return true;
        }
        currentNode = parentNode;
    }

    return false;
};

export const getNewNodeChildren = (state: BuilderState, node: BuildNode) => {
    if (node.type === "repeater") {
        const baseLayer = getBaseLayer(state);
        const repeaterNode = getComponent("repeaterDiv");
        repeaterNode.parent = node.id;
        applyDefaultStyles(repeaterNode, state);
        if (node.name === "productList" || node.name === "orderList" || node.name === "customerList") {
            repeaterNode.attr = {
                ...repeaterNode.attr,
                style: {
                    ...repeaterNode.attr?.style,
                    height: "100px",
                    flexDirection: "row"
                }
            };
        }

        baseLayer.components[repeaterNode.id] = repeaterNode;
        return [repeaterNode.id];
    }

    return undefined;
};

//think about using the context some day somehow
export const getContextDeep = (state: BuilderState, nodeId?: string): DynamicDataContext => {
    const baseLayer = getBaseLayer(state);
    if (baseLayer && (state.selectedNode || nodeId)) {
        let currentNode = null;
        let previousNodeId: string | null = null;
        if (nodeId) {
            currentNode = baseLayer.components[nodeId];
        } else if (state.selectedNode) {
            currentNode = baseLayer.components[state.selectedNode];
        }

        if (currentNode) {
            while (currentNode.id !== baseLayer.root) {
                if (currentNode.name === "smartTile") {
                    const variationIndex = currentNode.conditionalVariations?.findIndex(
                        cv => previousNodeId && cv.children?.includes(previousNodeId)
                    );
                    switch (variationIndex) {
                        case 2:
                        case 3:
                        case 4:
                        case 5:
                            return ActiveEntityType.SMART_GRID;
                        default:
                            return "general";
                    }
                }

                if (currentNode.name === "orderSection") {
                    return ActiveEntityType.ORDER;
                } else if (currentNode.name === "productSection") {
                    return ActiveEntityType.PRODUCT;
                } else if (currentNode.name === "customerSection") {
                    return ActiveEntityType.CUSTOMER;
                } else if (currentNode.type === "simpleCart" || currentNode.name === "cartSection") {
                    return ActiveEntityType.CART;
                }

                if (currentNode.name === "repeaterDiv" && currentNode?.parent) {
                    const parent = baseLayer.components[currentNode?.parent];
                    if (parent.query?.dataType) {
                        const repeaterType = parent.query.dataType;
                        if (repeaterType === QueryDataTables.PRODUCTS) {
                            return ActiveEntityType.PRODUCT;
                        } else if (repeaterType === QueryDataTables.CUSTOMERS) {
                            return ActiveEntityType.CUSTOMER;
                        } else if (repeaterType === QueryDataTables.ORDERS) {
                            return ActiveEntityType.ORDER;
                        }
                    }
                    return ActiveEntityType.PRODUCT; //line items - todo improve for future array dynamic datas
                } else if (currentNode.name === "orderData") {
                    return ActiveEntityType.ORDER;
                } else {
                    if (currentNode.parent) {
                        previousNodeId = currentNode.id;
                        currentNode = baseLayer.components[currentNode.parent];
                    } else {
                        return "general";
                    }
                }
            }
            if (currentNode.name === "globalBlockPrint") {
                return ActiveEntityType.ORDER;
            }
        }
    }

    return "general";
};

export const isBaseLayerRootNode = (node: BuildNode) => {
    return node?.name?.includes("baseLayer");
};

//Init Functions

export const buildInitialize = (): Build => {
    const newBuild: Build = {
        id: v4(),
        name: "Oliver Build",
        version: "1.0.0",
        size: { key: "fullHD" },
        pages: [layerInitialize(BaseLayerTypes.PAGES, "Home", undefined)],
        popups: [],
        cfds: [],
        previewImage: "",
        extraAttributes: {
            fonts: [{ name: "Poppins", url: "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" }]
        }
    };

    newBuild.specialLayers = {
        homepage: { id: newBuild.pages[0].id, name: newBuild.pages[0].name, type: BaseLayerTypes.PAGES },
        checkoutComplete: { id: newBuild.pages[0].id, name: "", action: "" }
    };

    return newBuild;
};

export const popupInitialize = (name: string): BaseLayer => {
    const baseLayerNode = getComponent("baseLayerPopup");

    return {
        id: v4(),
        name: name,
        root: baseLayerNode.id,
        components: { [baseLayerNode.id]: baseLayerNode }
    };
};

const baseLayerTypeComponentsMap = {
    [BaseLayerTypes.PAGES]: "baseLayerPage",
    [BaseLayerTypes.POPUPS]: "baseLayerPopup",
    [BaseLayerTypes.CFDS]: "baseLayerCFD"
};

export const layerInitialize = (type: BaseLayerTypes, name?: string, gBType?: GlobalBlockTypes): BaseLayer => {
    switch (gBType) {
        case GlobalBlockTypes.GB_PRINT:
            const GBPrint = getComponent("globalBlockPrint");
            return {
                id: v4(),
                name: "Global Block Print",
                root: GBPrint.id,
                components: { [GBPrint.id]: GBPrint }
            };
        default:
            const baseLayerNode = getComponent(baseLayerTypeComponentsMap[type] as BuildComponentKey);
            baseLayerNode.attr = { style: { fontFamily: "Poppins" } };

            return {
                id: v4(),
                name: name || "Home",
                root: baseLayerNode.id,
                components: { [baseLayerNode.id]: baseLayerNode }
            };
    }
};

export const getBuildSize = (type?: GlobalBlockTypes) => {
    switch (type) {
        case GlobalBlockTypes.GB_PRINT:
            return { key: "58MM" };
        default:
            return { key: "fullHD" };
    }
};

export const generateGBPrint = (): BaseLayer[] => {
    const pages: BaseLayer[] = [];
    const rootId = v4();
    pages.push({
        id: v4(),
        name: "",
        root: rootId,
        components: {
            [rootId]: {
                id: rootId,
                name: "globalBlockPrint",
                type: "component"
            }
        }
    });
    return pages;
};

// export const generateGB = (buildType: BuildTypes): BaseLayer[] => {

// }

export const generateGBCFD = (): BaseLayer[] => {
    const pages: BaseLayer[] = [];
    const rootId = v4();
    pages.push({
        id: v4(),
        name: "",
        root: rootId,
        components: {
            [rootId]: {
                id: rootId,
                name: "globalBlockCFD",
                type: "component"
            }
        }
    });
    return pages;
};

export const duplicateBaseLayer = (baseLayer: BaseLayer): BaseLayer => {
    const copy: BaseLayer = deepCopy(baseLayer);
    copy.id = v4();
    const idMap: { [key: string]: string } = {};
    Object.keys(copy.components).forEach(key => (idMap[key] = v4()));
    copy.root = idMap[copy.root];
    Object.keys(idMap).forEach(key => {
        copy.components[idMap[key]] = deepCopy(copy.components[key]);
        copy.components[idMap[key]].id = idMap[key];
        if (copy.components[idMap[key]].parent) {
            copy.components[idMap[key]].parent = idMap[copy.components[idMap[key]].parent as string];
        }
        if (copy.components[idMap[key]].children) {
            copy.components[idMap[key]].children?.forEach((child, index) => (copy.components[idMap[key]].children![index] = idMap[child]));
        }
        // Handle conditionalVariations - remap children IDs and nested variations
        if (copy.components[idMap[key]].conditionalVariations) {
            remapConditionalVariationIds(copy.components[idMap[key]].conditionalVariations!, idMap);
        }
        delete copy.components[key];
    });
    return copy;
};

export const convertPageBaseLayerToPopupBaseLayer = (baseLayer: BaseLayer): BaseLayer => {
    const newBaseLayer = duplicateBaseLayer(baseLayer);
    const rootElement = newBaseLayer.components[newBaseLayer.root];
    rootElement.name = "baseLayerPopup";
    //eslint-disable-next-line
    // @ts-ignore
    rootElement.backdropClose = true;
    if (!rootElement.attr) rootElement.attr = {};
    rootElement.attr.style = { ...rootElement.attr.style, backgroundColor: "white" };
    rootElement.nodeSize = { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM };
    (rootElement as any).closeButton = { active: true, icon: { src: "cross" } };
    return newBaseLayer as BaseLayer;
};

export function updateNodePropertyOnLayer(
    baseLayer: BaseLayer,
    nodeId: string,
    propertyPath: (string | number)[],
    value: any | { [key: string]: any },
    remove?: boolean
) {
    const selectedNode = baseLayer.components[nodeId];
    if (selectedNode) {
        let current: any = selectedNode;
        for (let i = 0; i < propertyPath.length; i++) {
            const property = propertyPath[i];
            if (i === propertyPath.length - 1) {
                if (remove) {
                    if (Array.isArray(current)) {
                        current.splice(property as number, 1);
                    } else if (typeof current === "object" && property in current) {
                        delete current[property as string];
                    }
                } else {
                    if (typeof value === "object" && !Array.isArray(value) && typeof current === "object") {
                        current[property as string] = {
                            ...current[property as string],
                            ...value
                        };
                    } else {
                        current[property] = value;
                    }
                }
            } else {
                if (typeof current[property] === "object") {
                    current = current[property];
                } else {
                    let newValue: any = Array.isArray(current) ? [] : {};
                    current[property] = newValue;
                    current = newValue;
                }
            }
        }
    }
}

export const getNodeWithChildrenRecursive = (baseLayer: BaseLayer, node: BuildNode, parentId: string): BuildNode[] => {
    let childNodes: BuildNode[] = [];
    let childIds: string[] = [];
    const nodeCopy: BuildNode = deepCopy(node);
    nodeCopy.parent = parentId;
    if (nodeCopy.children) {
        for (let i = 0; i < nodeCopy.children.length; i++) {
            if (baseLayer.components[nodeCopy.children[i]]) {
                const nestedChildren = getNodeWithChildrenRecursive(baseLayer, baseLayer.components[nodeCopy.children[i]], nodeCopy.id);
                childIds.push(nestedChildren[0].id);
                childNodes.push(...nestedChildren);
            }
        }
        nodeCopy.children = childIds;
    }

    // Handle conditionalVariations - recursively get all nodes from each variation's children
    if (nodeCopy.conditionalVariations) {
        for (const cv of nodeCopy.conditionalVariations) {
            if (cv.children) {
                const cvChildIds: string[] = [];
                for (let i = 0; i < cv.children.length; i++) {
                    if (baseLayer.components[cv.children[i]]) {
                        const nestedChildren = getNodeWithChildrenRecursive(baseLayer, baseLayer.components[cv.children[i]], nodeCopy.id);
                        cvChildIds.push(nestedChildren[0].id);
                        childNodes.push(...nestedChildren);
                    }
                }
                cv.children = cvChildIds;
            }
            // Handle nested conditionalVariations recursively
            if (cv.conditionalVariations) {
                processNestedConditionalVariations(cv.conditionalVariations, baseLayer, nodeCopy.id, childNodes);
            }
        }
    }

    return [nodeCopy, ...childNodes];
};

// Helper function to recursively process nested conditionalVariations
const processNestedConditionalVariations = (
    conditionalVariations: ConditionalVariation[],
    baseLayer: BaseLayer,
    parentId: string,
    childNodes: BuildNode[]
): void => {
    for (const cv of conditionalVariations) {
        if (cv.children) {
            const cvChildIds: string[] = [];
            for (let i = 0; i < cv.children.length; i++) {
                if (baseLayer.components[cv.children[i]]) {
                    const nestedChildren = getNodeWithChildrenRecursive(baseLayer, baseLayer.components[cv.children[i]], parentId);
                    cvChildIds.push(nestedChildren[0].id);
                    childNodes.push(...nestedChildren);
                }
            }
            cv.children = cvChildIds;
        }
        // Recursively handle deeper nested conditionalVariations
        if (cv.conditionalVariations) {
            processNestedConditionalVariations(cv.conditionalVariations, baseLayer, parentId, childNodes);
        }
    }
};

// Helper function to recursively remap IDs in nested conditionalVariations
const remapConditionalVariationIds = (conditionalVariations: ConditionalVariation[], idMap: { [key: string]: string }): void => {
    for (const cv of conditionalVariations) {
        // Remap the optional id field if it exists and is in the idMap
        if (cv.id && idMap[cv.id]) {
            cv.id = idMap[cv.id];
        }
        if (cv.children) {
            cv.children = cv.children.map(childId => idMap[childId] || childId);
        }
        // Recursively handle deeper nested conditionalVariations
        if (cv.conditionalVariations) {
            remapConditionalVariationIds(cv.conditionalVariations, idMap);
        }
    }
};

export const getAllDescendantsOfNode = (node: BuildNode, allNodes: BuildNode[]): BuildNode[] => {
    const descendants: BuildNode[] = [];
    if (!node.children) return descendants;

    node.children.forEach(childId => {
        const child = allNodes.find(n => n.id === childId);
        if (child) {
            descendants.push(child);
            descendants.push(...getAllDescendantsOfNode(child, allNodes));
        }
    });

    return descendants;
};

export const duplicateListOfNodes = (nodes: BuildNode[], newParentId: string, preserveIds?: boolean): BuildNode[] => {
    const idMap: { [key: string]: string } = {}; // Old ID to new ID map
    const duplicatedNodes: BuildNode[] = [];

    for (const node of nodes) {
        const nodeCopy: BuildNode = deepCopy(node);
        if (!preserveIds) {
            const newId = v4();
            idMap[node.id] = newId;
            nodeCopy.id = newId;
        } else {
            idMap[node.id] = node.id;
        }
        duplicatedNodes.push(nodeCopy);
    }
    if (preserveIds) {
        return duplicatedNodes;
    }

    for (const nodeCopy of duplicatedNodes) {
        if (nodeCopy.parent) {
            if (idMap[nodeCopy.parent]) {
                nodeCopy.parent = idMap[nodeCopy.parent];
            } else {
                nodeCopy.parent = newParentId;
            }
        }

        if (nodeCopy.children) {
            nodeCopy.children = nodeCopy.children.map(childId => idMap[childId]);
        }

        if (nodeCopy.conditionalVariations) {
            nodeCopy.conditionalVariations.map(cV => {
                if (cV.children) {
                    cV.children = cV.children.map(childId => idMap[childId]);
                }
                if (cV.conditionalVariations) {
                    remapConditionalVariationIds(cV.conditionalVariations, idMap);
                }
            });
        }

        if ((nodeCopy as any)?.target && idMap[(nodeCopy as any).target]) {
            (nodeCopy as any).target = idMap[(nodeCopy as any).target];
        }
    }

    return duplicatedNodes;
};
export function pasteNodes({
    state,
    baseLayer,
    targetParent,
    nodes,
    newIndex,
    preserveIds
}: {
    state: BuilderState;
    baseLayer: BaseLayer;
    targetParent: BuildNode;
    nodes: BuildNode[];
    newIndex?: number;
    preserveIds?: boolean;
}) {
    const newNodes = duplicateListOfNodes(nodes, targetParent.id, preserveIds);
    const relevantNodeChildren = newNodes.filter(n => n.parent === targetParent.id);
    const firstNewNode = relevantNodeChildren[0];
    const cvMode = state.conditionalVariationMode;
    //should only check the outside nodes, to avoid errors with preset or special nodes we created ourseleves
    //todo: paste them by dom order from external to internal? more comprehensive solution, consider simply sorting with sortBuildNodesByHierarchy
    for (const n of relevantNodeChildren) {
        if (!isValidParent(baseLayer, n, targetParent.id, undefined, !!cvMode?.id)) return;
    }

    for (const n of newNodes) {
        baseLayer.components[n.id] = n;
    }

    // Check if we're in conditional variation mode and should paste into CV children
    const targetNodeSplit = (state.selectedNode || state.hoveredNode?.id)?.split("|");
    const targetNodeCVIndex = targetNodeSplit?.[1];

    if (cvMode?.id === targetParent.id && (typeof cvMode.index === "number" || targetNodeCVIndex)) {
        const cvVariation = (
            targetNodeCVIndex
                ? targetParent.conditionalVariations?.[parseInt(targetNodeCVIndex)]
                : typeof cvMode.index === "number"
                ? targetParent.conditionalVariations?.[cvMode.index]
                : undefined
        ) as ConditionalVariation;

        if (cvVariation) {
            const cvChildren = cvVariation.children || [];
            let insertionIndex: number | undefined;

            if (typeof newIndex === "number") {
                insertionIndex = newIndex;
            } else if (state.selectedNode && targetNodeCVIndex) {
                const foundIndex = cvChildren.indexOf(state.selectedNode.split("|")[0]);
                if (foundIndex >= 0) {
                    insertionIndex = foundIndex;
                }
            }

            if (firstNewNode) state.selectedNode = firstNewNode.id;

            if (typeof insertionIndex === "number" && insertionIndex >= 0 && insertionIndex < cvChildren.length) {
                cvVariation.children = [
                    ...cvChildren.slice(0, insertionIndex),
                    ...relevantNodeChildren.map(n => n.id),
                    ...cvChildren.slice(insertionIndex)
                ];
            } else {
                cvVariation.children = [...cvChildren, ...relevantNodeChildren.map(n => n.id)];
            }
            return;
        }
    }

    // Default behavior: paste into regular children
    const oldChildren = targetParent.children || [];
    const insertionIndex = typeof newIndex === "number" ? newIndex : state.selectedNode ? oldChildren.indexOf(state.selectedNode.split("|")[0]) : 0;
    if (firstNewNode) state.selectedNode = firstNewNode.id;
    if (!targetParent.children) {
        targetParent.children = relevantNodeChildren.map(n => n.id);
    } else if (insertionIndex < 0 || insertionIndex >= oldChildren.length) {
        targetParent.children = [...oldChildren, ...relevantNodeChildren.map(n => n.id)];
    } else {
        targetParent.children = [
            ...oldChildren.slice(0, insertionIndex),
            ...relevantNodeChildren.map(n => n.id),
            ...oldChildren.slice(insertionIndex)
        ];
    }
}

export const duplicateNodeFromJSONString = (jsonNode: string, newParentId: string): BuildNode => {
    const node: BuildNode = JSON.parse(jsonNode);
    node.id = v4();
    node.parent = newParentId;
    if (node.children) {
        node.children = node.children.map((child: string) => {
            return duplicateNodeFromJSONString(child, node.id).id;
        });
    }
    return node;
};

export const getAllComponents = (state: BuilderState): BuildNode[] => {
    let components: BuildNode[] = [];
    for (const page of state.build.pages) {
        components = components.concat(Object.values(page.components));
    }
    for (const popup of state.build.popups) {
        components = components.concat(Object.values(popup.components));
    }

    for (const cfd of state.build.cfds) {
        components = components.concat(Object.values(cfd.components));
    }
    return components;
};

// export const removeDynamicData = (baseLayer: BaseLayer, nodeId: string) => {
//     if (baseLayer.components[nodeId]) {
//         baseLayer.components[nodeId].dynamicData = undefined;
//         if (baseLayer.components[nodeId].children) {
//             baseLayer.components[nodeId]?.children.forEach(child => removeDynamicData(baseLayer, child));
//         }
//     }
// };

/**
 * Handles UI reactions when a new node is added.
 * For example, this can include opening popups or changing side menus.
 */
export function addNodeUIReactions(nodeAdded: BuildNode) {
    const name = nodeAdded.name;

    if (name === "button") {
        store.dispatch(setActionFromNode(true));
        store.dispatch(setCurrentPopup("actions"));
    }

    // temporarily commented out per product team request

    // if (name === "productGrid") {
    //     store.dispatch(setTemplateLibraryFilter("productGrid"));
    //     store.dispatch(setCurrentPopup("templateLibrary"));

    // }
    // if (name === "productList") {
    //     store.dispatch(setTemplateLibraryFilter("productList"));
    //     store.dispatch(setCurrentPopup("templateLibrary"));

    // }

    store.dispatch(setLeftInstance("editElement"));
}

export function sortBuildNodesByHierarchy(nodes: BuildNode[]): BuildNode[] {
    const idToNode = new Map<string, BuildNode>();
    const childToDepth = new Map<string, number>();
    const depthBuckets: Map<number, BuildNode[]> = new Map();

    for (const node of nodes) {
        idToNode.set(node.id, node);
    }

    const queue: { node: BuildNode; depth: number }[] = [];

    for (const node of nodes) {
        if (!node.parent || !idToNode.has(node.parent)) {
            queue.push({ node, depth: 0 });
        }
    }

    while (queue.length > 0) {
        const { node, depth } = queue.shift()!;
        if (childToDepth.has(node.id)) continue;

        childToDepth.set(node.id, depth);

        if (!depthBuckets.has(depth)) {
            depthBuckets.set(depth, []);
        }
        depthBuckets.get(depth)!.push(node);

        const children = node.children || [];
        for (const childId of children) {
            const childNode = idToNode.get(childId);
            if (childNode) {
                queue.push({ node: childNode, depth: depth + 1 });
            }
        }
    }

    const sorted: BuildNode[] = [];
    const maxDepth = Math.max(...depthBuckets.keys());

    for (let d = 0; d <= maxDepth; d++) {
        const layer = depthBuckets.get(d);
        if (layer) sorted.push(...layer);
    }

    return sorted;
}

// Allows you to check if two nodes are identical while ignoring their external customName and the ids inside them
export const deepCompareNodesButNotIDsInsideThem = (a: Record<string, any>, b: Record<string, any>) => {
    // Regex for UUID v4
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

    if (a === b) return true;

    // 2. Check if both are UUIDs -> consider equal
    if (typeof a === "string" && uuidRegex.test(a) && typeof b === "string" && uuidRegex.test(b)) {
        return true;
    }

    if ((typeof a === "string" && uuidRegex.test(a)) || (typeof b === "string" && uuidRegex.test(b))) {
        return false;
    }

    // 4. Check for null/non-object types (after UUID check)
    if (a == null || typeof a !== "object" || b == null || typeof b !== "object") {
        return false;
    }

    if (Array.isArray(a) !== Array.isArray(b)) return false;

    if (Array.isArray(a)) {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (!deepCompareNodesButNotIDsInsideThem(a[i], b[i])) return false;
        }
        return true;
    }

    const keysA = Object.keys(a).filter(key => key !== "id");
    const keysB = Object.keys(b).filter(key => key !== "id");

    if (keysA.length !== keysB.length) return false;

    // Check if the set of relevant keys is the same (ignores order)
    const keysBSet = new Set(keysB);
    for (const key of keysA) {
        if (!keysBSet.has(key)) return false;
    }

    // Recursively compare values for each relevant key
    for (const key of keysA) {
        if (key === "customName") continue;
        if (!deepCompareNodesButNotIDsInsideThem(a[key], b[key])) return false;
    }

    return true;
};

export const leaveRootNodeMergeStyles = (rootNode: BuildNode, targetParentNode: BuildNode) => {
    if (rootNode.attr?.style) {
        const keysToIgnore = ["width", "height", "display", "borderRadius"];
        const valuesToIgnore = ["inherit", "initial", "unset"];
        const filteredStyle = Object.fromEntries(
            Object.entries(rootNode.attr.style).filter(
                ([key]) => !keysToIgnore.includes(key) && !valuesToIgnore.includes(rootNode.attr?.style?.[key as keyof CSSProperties] as string)
            )
        );
        const rootAttr = (targetParentNode.attr = targetParentNode.attr || ({ style: {} } as any));
        rootAttr.style = { ...(rootAttr.style || {}), ...filteredStyle } as any;
    }
};

export type LayerDetails = {
    type: BaseLayerTypes;
    index: number;
    layer: BaseLayer;
};

export function getCurrentLayer(): LayerDetails {
    const builderState = store.getState().builder;
    const currentLayerReference = builderState.currentLayer;
    const currentBaseLayer = builderState.build[currentLayerReference.type][currentLayerReference.index];
    return {
        type: currentLayerReference.type,
        index: currentLayerReference.index,
        layer: currentBaseLayer
    };
}

export const pasteImageFromClipboard = async (imageFile: File): Promise<boolean> => {
    const build = store.getState().builder.build;
    let finalUrl = "";
    try {
        if (build.companyId) {
            const result = await store
                .dispatch(attachmentsApi.endpoints.uploadAttachments.initiate({ companyId: build.companyId, files: [imageFile] }))
                .unwrap();
            finalUrl = result?.[0]?.url || "";
        }
    } catch (err) {
        console.error("Failed uploading clipboard image", err);
    }

    if (!finalUrl) {
        console.error("Clipboard image upload returned empty URL, aborting paste.");
        return false;
    }

    const imageNode = getComponent("image") as ImageNode;
    imageNode.image = { url: finalUrl, name: imageFile.name, id: v4() };
    const { addNode } = await import("@builder/store/slices/BuilderSlice/builderSlice");
    store.dispatch(addNode({ node: imageNode }));
    return true;
};

export const isClipboardImage = async (clipboard: any): Promise<File | null> => {
    if (!clipboard?.read) return null;
    try {
        const items = await clipboard.read();
        for (const item of items) {
            for (const type of item.types) {
                if (type.startsWith("image/")) {
                    const blob: Blob = await item.getType(type);
                    const ext = type.split("/")[1] || "png";
                    return new File([blob], `clipboard-image.${ext}`, { type });
                }
            }
        }
    } catch (err) {
        console.error("Error reading clipboard for image", err);
    }
    return null;
};

export const findInnerIndex = (conditionalVariations: ConditionalVariation[], nodeId: string) => {
    for (const cv of conditionalVariations) {
        const index = cv.children?.indexOf(nodeId);
        if (index !== -1) return index;
    }
    return -1;
};
